import { useEffect, useState } from 'react';

import { useAuthContext } from '@/contexts/auth-context';

import { fetchAndStoreWxJwt } from '../utils/wx-jwt-token';

/**
 * Hook that handles JWT fetching and returns readiness state.
 * For unified auth accounts: automatically fetches JWT and returns true when ready.
 * For non-unified auth accounts: returns false (no JWT needed).
 *
 * @returns {boolean} Whether JWT token is ready (or not needed)
 */
export function useWxJwtReady() {
  const { whoami } = useAuthContext();
  const [isJwtReady, setIsJwtReady] = useState(false);

  useEffect(() => {
    if (!whoami) {
      setIsJwtReady(false);
      return;
    }

    // If no unified auth required, JWT will NEVER be ready (and that's fine)
    if (!whoami.account_unified_auth) {
      setIsJwtReady(false);
      return;
    }

    // If unified auth is required, fetch JWT and wait for it to be ready
    let cancelled = false;

    const fetchAndWaitForJwt = async (): Promise<void> => {
      try {
        // Fetch JWT - this will resolve the internal promise when done
        const jwtPromise = fetchAndStoreWxJwt(whoami);
        const success = jwtPromise ? await jwtPromise : false;
        if (!cancelled) {
          setIsJwtReady(success);
        }
      } catch {
        // On error, JWT is not ready
        if (!cancelled) {
          setIsJwtReady(false);
        }
      }
    };

    void fetchAndWaitForJwt();

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
      setIsJwtReady(false);
    };
  }, [whoami]);

  return isJwtReady;
}
